import { useState } from 'react';
import { formatteerEuro, toonIban } from '@qc/core';
import { Knop } from '@qc/ui';
import { useTerugbetalingen, type TerugbetalingRij } from './useTerugbetalingen.js';

function datum(iso: string): string {
  return new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'Europe/Amsterdam',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function Rij({ rij }: { rij: TerugbetalingRij }) {
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3">
      <div>
        <p className="font-semibold">{rij.naam}</p>
        <p className="text-tekst-zacht">
          {toonIban(rij.iban)} · {rij.reden}
        </p>
      </div>
      <div className="text-right">
        <p className="text-xl font-semibold tabular-nums">{formatteerEuro(rij.bedragCent)}</p>
        <p className="text-tekst-zacht">
          {rij.uitbetaaldOp === null
            ? `vastgelegd ${datum(rij.aangemaaktOp)}`
            : `uitbetaald ${datum(rij.uitbetaaldOp)} · ${rij.batchId ?? ''}`}
        </p>
      </div>
    </li>
  );
}

/**
 * Wat de kassa's aan terugbetalingen hebben vastgelegd (pin-betaling die
 * niet via de kaart terug kan, docs/open-punten.md punt 8). Eén knop maakt
 * het SEPA-bestand voor de bank en zet alles op uitbetaald.
 */
export function Terugbetalingen() {
  const beeld = useTerugbetalingen();
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState<string | null>(null);

  const totaal = beeld.open.reduce((som, rij) => som + rij.bedragCent, 0);

  async function maakBatch() {
    setBezig(true);
    setMelding(null);
    try {
      const uitkomst = await beeld.maakBatch();
      setMelding(uitkomst.melding);
    } finally {
      setBezig(false);
    }
  }

  return (
    <section className="rounded-tegel border border-rand bg-vlak p-6">
      <h2 className="mb-1 text-2xl font-bold">Terugbetalingen aan klanten</h2>
      <p className="mb-4 text-tekst-zacht">
        Correcties op pin-betaalde bonnen. Binnen één werkdag overmaken: download het bestand en
        upload het bij Rabobank.
      </p>

      {beeld.fout !== null && <p className="mb-4 text-fout">{beeld.fout}</p>}
      {beeld.laden && <p className="text-tekst-zacht">Bezig met laden…</p>}

      {!beeld.laden && beeld.open.length === 0 && (
        <p className="mb-4 text-goed">Er staat niets open.</p>
      )}

      {beeld.open.length > 0 && (
        <>
          <ul className="mb-4 divide-y divide-rand">
            {beeld.open.map((rij) => (
              <Rij key={rij.id} rij={rij} />
            ))}
          </ul>
          <Knop nadruk="hoofd" className="w-full" disabled={bezig} onClick={() => void maakBatch()}>
            {bezig
              ? 'Bezig…'
              : `SEPA-bestand maken — ${beeld.open.length} × , ${formatteerEuro(totaal)}`}
          </Knop>
        </>
      )}

      {melding !== null && <p className="mt-4">{melding}</p>}

      {beeld.uitbetaald.length > 0 && (
        <details className="mt-6">
          <summary className="cursor-pointer text-tekst-zacht">
            Eerder uitbetaald ({beeld.uitbetaald.length})
          </summary>
          <ul className="mt-2 divide-y divide-rand">
            {beeld.uitbetaald.slice(0, 20).map((rij) => (
              <Rij key={rij.id} rij={rij} />
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
